

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [editMessage, setEditMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const itemsPerPage = 10;
  const cacheKey = 'messages_cache';
  const cacheTimeKey = 'messages_cache_time';

  const navigate = useNavigate();

  const fetchMessages = async (force = false) => {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);
      const now = Date.now();

      if (cachedData && cachedTime && !force) {
        const hoursDifference = (now - parseInt(cachedTime)) / (1000 * 60 * 60);
        if (hoursDifference < 3) {
          const data = JSON.parse(cachedData);
          setMessages(data);
          paginate(data, currentPage);
          return;
        }
      }

      // Fresh fetch
      const res = await axios.get('/api/v1/admin/messages', { withCredentials: true });
      localStorage.setItem(cacheKey, JSON.stringify(res.data));
      localStorage.setItem(cacheTimeKey, now.toString());
      setMessages(res.data);
      paginate(res.data, currentPage);

    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    paginate(messages, currentPage);
  }, [messages, currentPage]);

  const paginate = (data, page) => {
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setDisplayedMessages(data.slice(start, end));
    setTotalPages(Math.ceil(data.length / itemsPerPage));
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`/api/v1/admin/messages/${messageId}`, { withCredentials: true });
      fetchMessages(true); // Force fetch new after delete
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleEdit = (message) => {
    setEditMessage(message);
    setModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`/api/v1/admin/messages/${editMessage._id}`, editMessage, {
        withCredentials: true
      });
      fetchMessages(true); // Force fetch new after edit
      setModalOpen(false);
    } catch (err) {
      console.error('Edit error:', err);
      alert('Failed to update message');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-full ${currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'} mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (

    <div className="flex-1 p-4 md:p-8 bg-gray-100">
    <h2 className="text-xl md:text-2xl font-semibold mb-4">Messages</h2>
    <div className="bg-white p-4 md:p-6 rounded-xl shadow">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-gray-700 border-b">
              <th className="py-2 px-4">Sender ID</th>
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Message</th>
              <th className="py-2 px-4">Status</th>
              <th className="py-2 px-4">Date</th>
              <th className="py-2 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedMessages.map(msg => (
              <tr key={msg._id} className="border-b hover:bg-gray-50">
                <td className="py-2 px-4 truncate max-w-[120px]">{msg.senderId}</td>
                <td className="py-2 px-4 font-semibold">{msg.senderName}</td>
                <td className="py-2 px-4 max-w-[300px] truncate">{msg.message}</td>
                <td className="py-2 px-4">{msg.status}</td>
                <td className="py-2 px-4 whitespace-nowrap">
                  {new Date(msg.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  }).replace(/ /g, '-')}
                </td>
                <td className="py-2 px-4 space-x-2 whitespace-nowrap">
                  <button 
                    onClick={() => navigate(`/admin-dashboard/messages/${msg._id}`)} 
                    className="text-blue-600 hover:text-blue-800 p-1"
                  >
                    👁
                  </button>
                  <button 
                    onClick={() => handleEdit(msg)} 
                    className="text-yellow-600 hover:text-yellow-800 p-1"
                  >
                    ✏
                  </button>
                  <button 
                    onClick={() => handleDelete(msg._id)} 
                    className="text-red-600 hover:text-red-800 p-1"
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile List */}
      <div className="md:hidden space-y-4">
        {displayedMessages.map(msg => (
          <div key={msg._id} className="border-b p-4 last:border-b-0 hover:bg-gray-50">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <div className="font-semibold">{msg.senderName}</div>
                <div className="text-sm text-gray-500 truncate">ID: {msg.senderId}</div>
              </div>
              <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                {msg.status}
              </span>
            </div>
            <div className="text-sm mb-2 truncate">{msg.message}</div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {new Date(msg.createdAt).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                }).replace(/ /g, '-')}
              </div>
              <div className="flex space-x-3">
                <button 
                  onClick={() => navigate(`/admin-dashboard/messages/${msg._id}`)} 
                  className="text-blue-600 hover:text-blue-800"
                >
                  👁
                </button>
                <button 
                  onClick={() => handleEdit(msg)} 
                  className="text-yellow-600 hover:text-yellow-800"
                >
                  ✏
                </button>
                <button 
                  onClick={() => handleDelete(msg._id)} 
                  className="text-red-600 hover:text-red-800"
                >
                  🗑
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap justify-center mt-4 gap-2">
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index + 1}
            onClick={() => handlePageChange(index + 1)}
            className={`min-w-[36px] px-2 py-1 rounded text-sm
              ${currentPage === index + 1 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            {index + 1}
          </button>
        ))}
      </div>
    </div>

    {/* Edit Message Modal (Keep existing modal code) */}
    {modalOpen && (
      <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 p-4">
        <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-lg">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Edit Message</h3>
          <textarea
            value={editMessage.message}
            onChange={(e) => setEditMessage({ ...editMessage, message: e.target.value })}
            className="mb-4 p-2 border border-gray-300 rounded w-full h-32"
          />
          <div className="flex justify-end space-x-3">
            <button 
              onClick={() => setModalOpen(false)} 
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button 
              onClick={handleSaveEdit} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    )}
  </div>

  );
};

export default MessagesPage;
